// src/entities/authorComposer/components/common/AuthorDocumentationWarning.tsx
import React from "react";
import { observer } from "mobx-react";
import { Alert, Box, Typography } from "@mui/material";
import { resolveEntityFieldLabel } from "../../../../common/utils/resolveEntityFieldLabel";
import { useRootStore } from "../../../../store/contexts";
import type { LangStore } from "../../../../store/LangStore";
import type { EstadoAutor } from "./authorStatus";

/**
 * Lo que le impide al autor entrar en una documentación, con la misma cuenta
 * que la validación de la generación: datos faltantes, o sin IPI y sin DNI
 * entregado a SADAIC. Las etiquetas se resuelven como en el aviso de la
 * generación (resolveEntityFieldLabel): una sola forma de nombrar los campos.
 */
export function avisosDeDocumentacion(estado: EstadoAutor | null, langStore: LangStore): string[] {
  if (!estado) return [];
  const avisos: string[] = [];
  if (estado.missingFields.length > 0) {
    const etiquetas = estado.missingFields.map((f) => resolveEntityFieldLabel(langStore, "AuthorComposer", f)).join(", ");
    avisos.push(langStore.authorIncompleteWarning.replace("{fields}", () => etiquetas));
  }
  if (estado.withoutIpiOrDni) avisos.push(langStore.authorWithoutIpiOrDniWarning);
  return avisos;
}

export const AuthorDocumentationWarning: React.FC<{ estado: EstadoAutor | null }> = observer(({ estado }) => {
  const { langStore } = useRootStore();
  const avisos = avisosDeDocumentacion(estado, langStore);
  // La región role="status" está siempre, aun vacía (sin margen ni alto): un lector de pantalla
  // anuncia lo que aparece en una región que ya existe, no una que se inserta con su texto. Es
  // información de la página, no una alerta que deba interrumpir a quien la lee: el Alert va sin rol
  // propio (el suyo por omisión es "alert").
  return (
    <Box role="status" sx={{ width: "100%" }}>
      {avisos.length > 0 && (
        <Alert severity="warning" role="none" sx={{ width: "100%" }}>
          {avisos.map((aviso) => (
            <Typography key={aviso} variant="body2">
              {aviso}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
});
